import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { showApiError, showSuccess } from "@/lib/toast";
import { LinkedLicence } from "@/lib/types";
import { getLinkedLicences, removeLinkedLicence } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ROUTES } from "@/constants/routeConstants";
import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { DeleteRowConfirmationDialog } from "@/components/ax/dataTable/DeleteRowConfirmationDialog";
import { RequestNewLicenceButton } from "../dashboard/RequestNewLicenceButton";
import { PageLayout } from "@/components/PageLayout";
import { Button } from "@/components/ui/button";

const QUERY_KEY = "getLinkedLicences";

export const LinkedLicencesPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState<boolean>(true);

  const queryClient = useQueryClient();
  const removeLinkedLicenceMutation = useMutation({
    mutationFn: (firmLicenceGuid: string) => {
      return removeLinkedLicence(String(activeFirm?.firmGuid), firmLicenceGuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      showSuccess(t("common.deletedSuccess"));
    },
    onError: showApiError,
  });

  useBreadcrumbs([
    {
      name: showOnlyAvailable ? t("licence.available") : t("licence.inUse"),
      path: ROUTES.LINKED_LicenceS,
    },
  ]);

  const columns: ColumnDef<LinkedLicence>[] = [
    {
      accessorKey: "playerName",
      header: t("licence.playerName"),
      cell: ({ row }) => (
        <span className="text-xs truncate">{row.original.playerName ? row.original.playerName : "-"}</span>
      ),
    },
    {
      accessorKey: "licenceType",
      header: t("licence.licenceType"),
    },
    {
      accessorKey: "licenceExpiry",
      header: t("licence.licenceExpiry"),
      cell: ({ row }) => <span className="text-xs truncate">{row.original.licenceExpiry}</span>,
    },
    {
      accessorKey: "intId",
      header: () => "",
      cell: ({ row }) => {
        if (showOnlyAvailable) return null;
        return (
          <DeleteRowConfirmationDialog
            deleteText={t("linkedLicences.revokeLicence")}
            onConfirmDelete={() => {
              removeLinkedLicenceMutation.mutate(row.original.firmLicenceGuid);
            }}
            isLoading={
              removeLinkedLicenceMutation.isPending &&
              removeLinkedLicenceMutation.variables === row.original.firmLicenceGuid
            }
          />
        );
      },
    },
  ];

  if (!activeFirm) {
    return <div>{t("common.noFirmSelected")}</div>;
  }

  return (
    <PageLayout
      title={<>{t("Licences")}</>}
      description={t("linkedLicences.linkedLicencesDesc")}
      extraRight={<RequestNewLicenceButton />}
    >
      <AxDataTable
        columns={columns}
        fetcher={getLinkedLicences}
        getRowId={(row) => row.intId.toString()}
        uniqQueryKey={QUERY_KEY}
        dynamicRequestParams={{
          Available: showOnlyAvailable ? 0 : 1,
          FirmGuid: activeFirm.firmGuid,
        }}
        extraHeader={
          <Button size="sm" variant="outline" onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}>
            {showOnlyAvailable ? t("licence.showInUse") : t("licence.showAvailable")}
          </Button>
        }
      />
    </PageLayout>
  );
};
